import { useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, ChevronDown } from "lucide-react";
import SpyCleanLogo from "./SpyCleanLogo";

const navLinks = [
  { label: "Leistungen", to: "/leistungen" },
  { label: "Einsatzgebiete", to: "/standorte/" },
  { label: "Preise", to: "/preise" },
  { label: "Blog", to: "/blog" },
  { label: "FAQ", to: "/faq" },
  { label: "Termin verwalten", to: "/termin" },
];

const openCookieSettings = () => {
  window.dispatchEvent(new CustomEvent("spyclean-open-consent-settings"));
};

function MobileAccordion({
  title,
  links,
}: {
  title: string;
  links: { label: string; to: string }[];
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="md:hidden">
      <button
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="relative w-full flex items-center justify-center gap-3 py-4 border-b border-gray-200/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary rounded"
      >
        <span className="text-[15px] font-bold text-primary">{title}</span>
        <ChevronDown
          className={`w-5 h-5 text-primary transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <ul className="py-3 grid grid-cols-2 gap-x-4 gap-y-1 text-center">
          {links.map((link, i) => (
            <li key={i}>
              <Link
                to={link.to}
                className="inline-flex min-h-[44px] items-center justify-center text-[14px] text-primary/80 font-medium hover:text-primary transition-colors"
              >
                {link.label}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <>
      <h3 className="text-[14px] font-bold text-primary uppercase tracking-[0.08em] mb-0">
        {children}
      </h3>
      <div className="h-[2px] w-[32px] bg-secondary rounded-full mt-2.5 mb-4 mx-auto md:mx-0" />
    </>
  );
}

export default function Footer() {
  const year = new Date().getFullYear();

  return (
    <footer className="bg-[#F4F7FB] relative">
      {/* Thin yellow separator at top */}
      <div
        className="w-full"
        style={{ height: "3px", background: "#FBC309", margin: 0, padding: 0 }}
      />

      <div className="mx-auto w-full max-w-[1200px] px-5 md:px-6 py-8 md:py-10 box-border">
        {/* Desktop grid — 4 columns: brand / nav / location / contact */}
        <div
          className="hidden md:grid md:grid-cols-[1.1fr_1fr_1fr_1.1fr]"
          style={{ columnGap: "44px", alignItems: "start" }}
        >
          {/* Area 1: Logo */}
          <div className="flex flex-col" style={{ minWidth: 0 }}>
            <Link
              to="/"
              aria-label="Spy Clean GmbH – Startseite"
              className="inline-block"
            >
              <SpyCleanLogo
                alt="Spy Clean GmbH – Perfekt bis ins Detail"
                width="260"
                height="70"
                className="w-[260px] h-auto"
              />
            </Link>
          </div>

          {/* Area 2: Navigation */}
          <div style={{ minWidth: 0 }}>
            <SectionTitle>Navigation</SectionTitle>
            <ul className="grid grid-cols-2 gap-x-5 gap-y-2.5">
              {navLinks.map((link, i) => (
                <li key={i}>
                  <Link
                    to={link.to}
                    className="text-[14px] text-primary/80 font-medium hover:text-primary hover:underline underline-offset-4 transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Area 3: Standort & Zeiten */}
          <div style={{ minWidth: 0 }}>
            <SectionTitle>Standort &amp; Zeiten</SectionTitle>
            <ul className="space-y-3">
              <li className="flex items-start gap-2.5">
                <MapPin className="w-[18px] h-[18px] shrink-0 text-primary/70 mt-0.5" />
                <span className="text-[14px] text-primary/80 font-medium leading-[1.45]">
                  Lochhamer Str. 75
                  <br />
                  82166 Gräfelfing
                </span>
              </li>
              <li className="flex items-center gap-2.5">
                <Clock className="w-[18px] h-[18px] shrink-0 text-primary/70" />
                <span className="text-[14px] text-primary/80 font-medium">
                  Mo–Fr 08:00–17:00
                </span>
              </li>
            </ul>
          </div>

          {/* Area 4: Kontakt */}
          <div style={{ minWidth: 0 }}>
            <SectionTitle>Kontakt</SectionTitle>
            <ul className="space-y-3">
              <li className="flex items-center gap-2.5">
                <Phone className="w-[18px] h-[18px] shrink-0 text-primary/70" />
                <a
                  href="tel:+498974046177"
                  className="text-[14px] text-primary/80 font-medium hover:text-primary transition-colors"
                >
                  089 74046177
                </a>
              </li>
              <li className="flex items-center gap-2.5">
                <Phone className="w-[18px] h-[18px] shrink-0 text-primary/70" />
                <a
                  href="tel:+4917664017221"
                  className="text-[14px] text-primary/80 font-medium hover:text-primary transition-colors"
                >
                  +49 176 640 17 221
                </a>
              </li>
              <li className="flex items-center gap-2.5">
                <Mail className="w-[18px] h-[18px] shrink-0 text-primary/70" />
                <a
                  href="mailto:info@spyclean.de"
                  className="text-[14px] text-primary/80 font-medium hover:text-primary transition-colors break-all"
                >
                  info@spyclean.de
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Mobile layout */}
        <div className="md:hidden text-center">
          {/* Brand + contact block */}
          <div className="mb-7 flex flex-col items-center gap-4">
            <Link
              to="/"
              aria-label="Spy Clean GmbH Startseite"
              className="inline-block"
            >
              <SpyCleanLogo
                alt="Spy Clean GmbH Logo"
                width="300"
                height="80"
                className="w-[240px] max-w-full h-auto"
              />
            </Link>
            <ul className="w-full space-y-1">
              <li className="flex min-h-[40px] items-center gap-2.5 justify-center">
                <Phone className="w-[18px] h-[18px] shrink-0 text-primary/70" />
                <a
                  href="tel:+498974046177"
                  className="text-[14px] text-primary/80 font-medium"
                >
                  089 74046177
                </a>
              </li>
              <li className="flex min-h-[40px] items-center gap-2.5 justify-center">
                <Phone className="w-[18px] h-[18px] shrink-0 text-primary/70" />
                <a
                  href="tel:+4917664017221"
                  className="text-[14px] text-primary/80 font-medium"
                >
                  +49 176 640 17 221
                </a>
              </li>
              <li className="flex min-h-[40px] items-center gap-2.5 justify-center">
                <Mail className="w-[18px] h-[18px] shrink-0 text-primary/70" />
                <a
                  href="mailto:info@spyclean.de"
                  className="text-[14px] text-primary/80 font-medium"
                >
                  info@spyclean.de
                </a>
              </li>
            </ul>
          </div>

          {/* Standort & Zeiten — always visible */}
          <div className="mb-5 border-t border-gray-200/70 pt-6">
            <SectionTitle>Standort &amp; Zeiten</SectionTitle>
            <ul className="space-y-2.5">
              <li className="flex items-start justify-center gap-2.5">
                <MapPin className="w-[18px] h-[18px] shrink-0 text-primary/70 mt-0.5" />
                <span className="text-[14px] text-primary/80 font-medium leading-[1.45]">
                  Lochhamer Str. 75
                  <br />
                  82166 Gräfelfing
                </span>
              </li>
              <li className="flex items-center justify-center gap-2.5">
                <Clock className="w-[18px] h-[18px] shrink-0 text-primary/70" />
                <span className="text-[14px] text-primary/80 font-medium">
                  Mo–Fr 08:00–17:00
                </span>
              </li>
            </ul>
          </div>

          {/* Accordion */}
          <MobileAccordion title="Navigation" links={navLinks} />
        </div>
      </div>

      {/* Bottom bar — legal links only here */}
      <div
        className="border-t border-gray-200/60"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div
          className="mx-auto w-full max-w-[1200px] px-5 md:px-6"
          style={{ paddingTop: "14px", paddingBottom: "16px" }}
        >
          <div className="flex flex-col items-center gap-2 md:flex-row md:justify-between">
            <p className="text-[12px] text-gray-500 text-center md:text-left">
              &copy; {year} Spy Clean GmbH. Alle Rechte vorbehalten.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
              <Link
                to="/impressum"
                className="text-[12px] text-gray-500 hover:text-primary transition-colors"
              >
                Impressum
              </Link>
              <Link
                to="/datenschutz"
                className="text-[12px] text-gray-500 hover:text-primary transition-colors"
              >
                Datenschutz
              </Link>
              <Link
                to="/agb"
                className="text-[12px] text-gray-500 hover:text-primary transition-colors"
              >
                AGB
              </Link>
              <Link
                to="/widerruf-uebersicht"
                className="text-[12px] text-gray-500 hover:text-primary transition-colors"
              >
                Widerruf
              </Link>
              <button
                onClick={openCookieSettings}
                className="text-[12px] text-gray-500 hover:text-primary transition-colors"
              >
                Cookie-Einstellungen
              </button>
            </div>
          </div>
        </div>
        {/* Mobile spacer for sticky bottom bar — placed after legal links so nothing is covered */}
        <div className="md:hidden" style={{ height: "82px" }} />
      </div>
    </footer>
  );
}
